import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLinkedInAccount } from '../hooks/useLinkedInAccount';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit,
  Trash2,
  Clock,
  User,
  Users,
  Menu,
  Filter,
  X,
  Eye,
  // ExternalLink,
  ThumbsUp,
  Repeat2,
  MessageCircle,
  MoreHorizontal
} from 'lucide-react';
import Sidebar from './Sidebar';
import Header from './Header';

const SchedulePostsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('scheduled-posts');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showPostPreview, setShowPostPreview] = useState(false);
  const [previewPost, setPreviewPost] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    scheduled: false,
    posted: false,
    upcoming: false
  });
  const { accountId, accountType } = useLinkedInAccount();

  // --- Group posting state ---
  const [linkedinGroups, setLinkedinGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [isScrapingGroups, setIsScrapingGroups] = useState(false);
  const [showAddGroupForm, setShowAddGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupUrl, setNewGroupUrl] = useState('');
  const [isAddingGroup, setIsAddingGroup] = useState(false);
  const [groupWindowMinutes, setGroupWindowMinutes] = useState(180);
  const [groupStartDateTime, setGroupStartDateTime] = useState(() => {
    // Default to "now" in a format <input type="datetime-local"> accepts
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  });
  const [isPostingToGroups, setIsPostingToGroups] = useState(false);
  const [showGroupPanel, setShowGroupPanel] = useState(false);
  const [modalGroupBatch, setModalGroupBatch] = useState(null);
  const [groupBatches, setGroupBatches] = useState([]);
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  const [expandedBatchDetail, setExpandedBatchDetail] = useState(null);
  const [isLoadingBatchDetail, setIsLoadingBatchDetail] = useState(false);
  const [retryingJobId, setRetryingJobId] = useState(null);
  const [isLoadingBatches, setIsLoadingBatches] = useState(false);

  // useEffect(() => {
  //   fetchScheduledPosts();
  //   const interval = setInterval(fetchScheduledPosts, 30000);
  //   return () => clearInterval(interval);
  // }, [currentDate]);
const fetchScheduledPosts = useCallback(async () => {
  try {
    setLoading(true);
    
    // ✅ GET userId FROM localStorage
    const userId = localStorage.getItem('userId');
    
    // ✅ Check if both userId and accountId exist
    if (!userId || !accountId) {
      console.log('[SCHEDULE] Missing userId or accountId:', { userId, accountId });
      setScheduledPosts([]);
      setLoading(false);
      return;
    }

    console.log('[SCHEDULE] Fetching posts for:', { 
      userId: parseInt(userId), 
      accountId,
      month: currentDate.getMonth() + 1,
      year: currentDate.getFullYear() 
    });

    // ✅ FIXED URL - Removed 'webhook-test'
    const response = await fetch('https://n8n.acespireconsulting.com/webhook/webhook/get-scheduled-posts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId: parseInt(userId),
        linkedinAccountId: accountId,
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
        filters: selectedFilters
      })
    });

    console.log('[SCHEDULE] Response status:', response.status);

    if (response.ok) {
      const data = await response.json();
      console.log('[SCHEDULE] Received data:', data);
      
      let posts = Array.isArray(data) ? data : (data.posts || []);
      
      // Apply client-side filtering if any filters are selected
      const hasActiveFilters = selectedFilters.scheduled || selectedFilters.posted || selectedFilters.upcoming;
      
      if (hasActiveFilters) {
        posts = posts.filter(post => {
          const now = new Date();
          const postDate = new Date(post.scheduled_time || post.posted_time);
          
          // If no filters selected, show all
          if (!selectedFilters.scheduled && !selectedFilters.posted && !selectedFilters.upcoming) {
            return true;
          }
          
          // Check each filter
          let matchesFilter = false;
          
          if (selectedFilters.scheduled && post.status === 'scheduled') {
            matchesFilter = true;
          }
          
          if (selectedFilters.posted && post.status === 'posted') {
            matchesFilter = true;
          }
          
          if (selectedFilters.upcoming && post.status === 'scheduled' && postDate > now) {
            matchesFilter = true;
          }
          
          return matchesFilter;
        });
      }
      
      const transformedPosts = posts.map(post => {
        const postDateTime = new Date(post.scheduled_time || post.posted_time);
        
        return {
          ...post,
          date: postDateTime,
          title: post.prompt || post.content?.substring(0, 50) + '...',
          color: post.status === 'posted' ? 'bg-green-100 border-green-300' : 'bg-yellow-100 border-yellow-300',
          time: postDateTime.toLocaleTimeString('en-IN', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true,
            timeZone: 'Asia/Kolkata'
          }),
          displayDate: postDateTime.toLocaleDateString('en-IN', {
            timeZone: 'Asia/Kolkata'
          })
        };
      });
      
      console.log('[SCHEDULE] Transformed posts:', transformedPosts.length);
      setScheduledPosts(transformedPosts);
    } else {
      console.error('[SCHEDULE] Response not OK:', response.status);
      const errorText = await response.text();
      console.error('[SCHEDULE] Error response:', errorText);
    }
  } catch (error) {
    console.error('[SCHEDULE] Error fetching scheduled posts:', error);
  } finally {
    setLoading(false);
  }
}, [currentDate, selectedFilters, accountId]); // ✅ ADDED accountId to dependencies

  // --- Group posting handlers ---
  const N8N_BASE = 'https://n8n.acespireconsulting.com/webhook';

  const fetchGroups = async () => {
    if (!accountId) return;
    setIsLoadingGroups(true);
    try {
      const response = await fetch(`${N8N_BASE}/get-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linkedinAccountId: parseInt(accountId) }),
      });
      const data = await response.json();
      if (data.success) setLinkedinGroups(Array.isArray(data.groups) ? data.groups : []);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  const scrapeGroupsFromLinkedIn = async () => {
    if (!accountId) {
      alert('Connect a LinkedIn account first.');
      return;
    }
    const userId = localStorage.getItem('userId');
    setIsScrapingGroups(true);
    try {
      const response = await fetch(`${N8N_BASE}/scrape-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ linkedinAccountId: parseInt(accountId), user_id: userId }),
      });
      const data = await response.json();
      if (data.success) {
        await fetchGroups();
      } else {
        alert('Could not refresh groups from LinkedIn: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error refreshing groups: ' + error.message);
    } finally {
      setIsScrapingGroups(false);
    }
  };

  const addGroupManually = async () => {
    const userId = localStorage.getItem('userId');
    if (!newGroupUrl.trim()) {
      alert('Enter a LinkedIn group URL.');
      return;
    }
    setIsAddingGroup(true);
    try {
      const response = await fetch(`${N8N_BASE}/add-group-manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          linkedinAccountId: parseInt(accountId),
          user_id: userId,
          group_name: newGroupName,
          group_url: newGroupUrl,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setNewGroupName('');
        setNewGroupUrl('');
        setShowAddGroupForm(false);
        await fetchGroups();
      } else {
        alert('Could not add group: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error adding group: ' + error.message);
    } finally {
      setIsAddingGroup(false);
    }
  };

  const toggleGroupSelection = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId]
    );
  };

  // Posts the currently previewed calendar post (scheduled or already-posted)
  // to the selected groups, reusing its existing content/media.
  const postPreviewToGroups = async () => {
    if (!previewPost) return;
    if (selectedGroupIds.length === 0) {
      alert('Select at least one group to post to.');
      return;
    }
    const userId = localStorage.getItem('userId');
    setIsPostingToGroups(true);
    try {
      const response = await fetch(`${N8N_BASE}/post-to-groups-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          linkedinAccountId: parseInt(accountId),
          content: previewPost.content,
          media_url: previewPost.media_url || null,
          groupIds: selectedGroupIds,
          windowMinutes: groupWindowMinutes,
          scheduledStartTime: new Date(groupStartDateTime).toISOString(),
        }),
      });
      const data = await response.json();
      if (data.success) {
        setModalGroupBatch(data);
        fetchGroupBatches();
      } else {
        alert('Could not start group posting: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error posting to groups: ' + error.message);
    } finally {
      setIsPostingToGroups(false);
    }
  };

  const fetchGroupBatches = useCallback(async () => {
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    setIsLoadingBatches(true);
    try {
      const response = await fetch(`${N8N_BASE}/get-group-batches`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      const data = await response.json();
      if (data.success) setGroupBatches(Array.isArray(data.batches) ? data.batches : []);
    } catch (error) {
      console.error('Failed to fetch group batches:', error);
    } finally {
      setIsLoadingBatches(false);
    }
  }, []);

  const fetchBatchDetail = useCallback(async (batchId) => {
    setIsLoadingBatchDetail(true);
    try {
      const response = await fetch(`${N8N_BASE}/get-group-batch-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ batchId }),
      });
      const data = await response.json();
      if (data.success) setExpandedBatchDetail(data);
    } catch (error) {
      console.error('Failed to fetch batch detail:', error);
    } finally {
      setIsLoadingBatchDetail(false);
    }
  }, []);

  const openBatchDetail = (batchId) => {
    setExpandedBatchId(batchId);
    setExpandedBatchDetail(null);
    fetchBatchDetail(batchId);
  };

  const closeBatchDetail = () => {
    setExpandedBatchId(null);
    setExpandedBatchDetail(null);
  };

  const retryGroupJob = async (jobId) => {
    setRetryingJobId(jobId);
    try {
      const response = await fetch(`${N8N_BASE}/retry-group-job`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jobId }),
      });
      const data = await response.json();
      if (data.success) {
        if (expandedBatchId) await fetchBatchDetail(expandedBatchId);
        fetchGroupBatches();
      } else {
        alert('Could not retry: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      alert('Error retrying job: ' + error.message);
    } finally {
      setRetryingJobId(null);
    }
  };

  // Load group directory the first time the panel is opened inside the modal
  useEffect(() => {
    if (showGroupPanel && linkedinGroups.length === 0) {
      fetchGroups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGroupPanel]);

  // Load the batch list on mount, and keep polling only while something is
  // actually in progress -- stops itself once everything reaches a terminal
  // status so the calendar page isn't hammering n8n indefinitely.
  useEffect(() => {
    fetchGroupBatches();
  }, [fetchGroupBatches]);

  useEffect(() => {
    const hasActiveBatch = groupBatches.some((b) => b.status === 'in_progress');
    if (!hasActiveBatch) return;
    const interval = setInterval(fetchGroupBatches, 20000);
    return () => clearInterval(interval);
  }, [groupBatches, fetchGroupBatches]);

  // Keep the expanded batch's detail view live while it's still in
  // progress, so failures/successes update without a manual refresh.
  // Stops itself the moment the batch reaches a terminal status.
  useEffect(() => {
    if (!expandedBatchId || expandedBatchDetail?.batch?.status !== 'in_progress') return;
    const interval = setInterval(() => fetchBatchDetail(expandedBatchId), 15000);
    return () => clearInterval(interval);
  }, [expandedBatchId, expandedBatchDetail, fetchBatchDetail]);

  const closePreviewModal = () => {
    setShowPostPreview(false);
    setShowGroupPanel(false);
    setModalGroupBatch(null);
    setSelectedGroupIds([]);
  };
// Add listener for account changes
useEffect(() => {
  const handleAccountChange = () => {
    fetchScheduledPosts();
  };

  window.addEventListener('accountChanged', handleAccountChange);
  return () => window.removeEventListener('accountChanged', handleAccountChange);
}, [fetchScheduledPosts]);
// ← ADD selectedFilters dependency
useEffect(() => {
  fetchScheduledPosts();
  // const interval = setInterval(fetchScheduledPosts, 60000);
  // return () => clearInterval(interval);
}, [fetchScheduledPosts]);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    return { firstDay, daysInMonth };
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const getPostsForDate = (day) => {
    return scheduledPosts.filter(post => {
      const postDate = post.date;
      return postDate.getDate() === day &&
             postDate.getMonth() === currentDate.getMonth() &&
             postDate.getFullYear() === currentDate.getFullYear();
    });
  };

  const handleAddNewPost = () => {
    navigate('/create-post');
  };

  const handleDateDoubleClick = (day) => {
  const posts = getPostsForDate(day);
  if (posts.length === 1) {
    setPreviewPost(posts[0]);
    setShowPostPreview(true);
  }
  // Don't auto-show preview for multiple posts - let user click individual posts
};

const handlePostClick = (post, event) => {
  event.stopPropagation();
  
  // If clicking the same post, show preview
  if (selectedPost?.id === post.id) {
    setPreviewPost(post);
    setShowPostPreview(true);
  } else {
    // Otherwise just select it
    setSelectedPost(post);
  }
};

// Add a new function to handle double-click on individual posts
const handlePostDoubleClick = (post, event) => {
  event.stopPropagation();
  setPreviewPost(post);
  setShowPostPreview(true);
};

  const handleEditPost = () => {
    if (selectedPost) {
      navigate(`/create-post?edit=${selectedPost.id}`);
    }
  };

  const handleReschedule = () => {
    if (selectedPost) {
      navigate(`/create-post?reschedule=${selectedPost.id}`);
    }
  };

  const handleDeletePost = async () => {
  if (!selectedPost) return;
  
  if (window.confirm('Are you sure you want to delete this scheduled post?')) {
    try {
      const response = await fetch('https://n8n.acespireconsulting.com/webhook/delete-scheduled-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ postId: selectedPost.id })
      });
      
      const result = await response.json();
      
      if (response.ok && result.success) {
        // Success - show message and refresh
        alert('Post deleted successfully!');
        
        // Close preview modal if open
        setShowPostPreview(false);
        
        // Clear selected post
        setSelectedPost(null);
        
        // Refresh the calendar
        fetchScheduledPosts();
      } else {
        // Error from backend
        alert(result.message || 'Failed to delete post. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Network error: Failed to delete post. Please check your connection and try again.');
    }
  }
};

  const toggleFilter = (filterName) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  const applyFilters = () => {
    fetchScheduledPosts();
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentDate);
  const blanks = Array(firstDay).fill(null);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  // Post Preview Modal Component
  const PostPreviewModal = () => {
  if (!showPostPreview || !previewPost) return null;

  // Add delete handler specifically for the modal
  const handleDeleteFromModal = async () => {
    if (!previewPost) return;
    
    if (window.confirm('Are you sure you want to delete this scheduled post?')) {
      try {
        const response = await fetch('https://n8n.acespireconsulting.com/webhook/delete-scheduled-post', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ postId: previewPost.id })
        });
        
        const result = await response.json();
        
        if (response.ok && result.success) {
          alert('Post deleted successfully!');
          
          // Close the modal
          setShowPostPreview(false);
          
          // Clear selected post
          setSelectedPost(null);
          
          // Refresh calendar
          fetchScheduledPosts();
        } else {
          alert(result.message || 'Failed to delete post. Please try again.');
        }
      } catch (error) {
        console.error('Error deleting post:', error);
        alert('Network error: Failed to delete post. Please check your connection and try again.');
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Post Preview</h2>
          <button
            onClick={closePreviewModal}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Post Preview Card */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mr-3">
                <span className="text-white text-sm font-semibold">AC</span>
              </div>
              <div>
                <h3 className="font-semibold text-sm">Acespire Consulting</h3>
                <p className="text-xs text-gray-500">
                  {previewPost.status === 'posted' ? '2h' : 'Scheduled'} • 3rd
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button className="text-blue-600 text-sm hover:underline">Follow</button>
              <button className="text-gray-400">
                <MoreHorizontal className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="mb-3">
            <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">
              {previewPost.content}
            </p>
            <button className="text-blue-600 text-sm hover:underline mt-1">See translation</button>
          </div>

          {previewPost.media_url && (
            <div className="mb-3">
              <img 
                src={previewPost.media_url} 
                alt="Post media" 
                className="w-full rounded-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <p className="text-xs text-gray-600 mt-2">
                Generated with {previewPost.tone_style || 'professional'} tone for {previewPost.post_objective || 'engagement'} objective
              </p>
            </div>
          )}

          <div className="flex items-center justify-between text-gray-500 text-xs border-t pt-3">
            <div className="flex items-center space-x-4">
              <button className="flex items-center hover:text-blue-600">
                <ThumbsUp className="w-4 h-4 mr-1" />
                0
              </button>
              <button className="flex items-center hover:text-green-600">
                <Repeat2 className="w-4 h-4 mr-1" />
                0
              </button>
              <button className="flex items-center hover:text-purple-600">
                <MessageCircle className="w-4 h-4 mr-1" />
                0
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-3">
          {previewPost.status === 'scheduled' && (
            <button
              onClick={handleDeleteFromModal} // ← UPDATED: Use dedicated modal handler
              className="flex-1 flex items-center justify-center px-4 py-2 text-red-600 border border-red-600 rounded-lg hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </button>
          )}
          <button
            onClick={() => {
              setShowGroupPanel((prev) => !prev);
              setModalGroupBatch(null);
              setSelectedGroupIds([]);
            }}
            className="flex-1 flex items-center justify-center px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50"
          >
            <Users className="w-4 h-4 mr-2" />
            {showGroupPanel ? 'Hide Group Posting' : 'Post to Groups'}
          </button>
        </div>

        {showGroupPanel && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-medium text-sm">Select Groups</h3>
              <button
                onClick={scrapeGroupsFromLinkedIn}
                disabled={isScrapingGroups}
                className="text-xs text-blue-600 hover:underline disabled:opacity-50"
              >
                {isScrapingGroups ? 'Refreshing...' : 'Refresh from LinkedIn'}
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              This post's content will be posted to the groups you select, spread out over the window below.
            </p>

            {isLoadingGroups ? (
              <div className="text-xs text-gray-500 py-3 text-center">Loading groups...</div>
            ) : linkedinGroups.length === 0 ? (
              <div className="text-xs text-gray-500 py-3 text-center border border-dashed border-gray-300 rounded-lg">
                No groups yet. Click "Refresh from LinkedIn" or add one manually below.
              </div>
            ) : (
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                {linkedinGroups.map((group) => (
                  <label key={group.id} className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedGroupIds.includes(group.id)}
                      onChange={() => toggleGroupSelection(group.id)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 truncate">{group.group_name}</p>
                      <p className="text-xs text-gray-400">{group.source === 'scraped' ? 'From LinkedIn' : 'Added manually'}</p>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {!showAddGroupForm ? (
              <button
                onClick={() => setShowAddGroupForm(true)}
                className="mt-2 text-xs text-blue-600 hover:underline"
              >
                + Add a group manually
              </button>
            ) : (
              <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-2">
                <input
                  type="text"
                  placeholder="Group name"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  placeholder="https://www.linkedin.com/groups/12345678/"
                  value={newGroupUrl}
                  onChange={(e) => setNewGroupUrl(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={addGroupManually}
                    disabled={isAddingGroup}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    {isAddingGroup ? 'Adding...' : 'Add Group'}
                  </button>
                  <button
                    onClick={() => { setShowAddGroupForm(false); setNewGroupName(''); setNewGroupUrl(''); }}
                    className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">Start Time</label>
              <input
                type="datetime-local"
                value={groupStartDateTime}
                onChange={(e) => setGroupStartDateTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1">The first selected group posts at exactly this time.</p>
            </div>

            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">Spread the rest over</label>
              <select
                value={groupWindowMinutes}
                onChange={(e) => setGroupWindowMinutes(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={60}>1 hour</option>
                <option value={120}>2 hours</option>
                <option value={180}>3 hours</option>
                <option value={240}>4 hours</option>
              </select>
            </div>

            {!modalGroupBatch ? (
              <button
                onClick={postPreviewToGroups}
                disabled={isPostingToGroups || selectedGroupIds.length === 0}
                className="w-full mt-3 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isPostingToGroups
                  ? 'Starting...'
                  : `Post to ${selectedGroupIds.length} Group${selectedGroupIds.length !== 1 ? 's' : ''}`}
              </button>
            ) : (
              <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs font-medium text-blue-800">
                  Posting to {modalGroupBatch.total_groups} group{modalGroupBatch.total_groups !== 1 ? 's' : ''} over the next {(modalGroupBatch.window_minutes / 60).toFixed(1)} hours — check the "Group Posts" panel for live progress.
                </p>
              </div>
            )}
          </div>
        )}

        <button
          onClick={closePreviewModal}
          className="w-full mt-4 px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
        >
          Close
        </button>
      </div>
    </div>
  );
};

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />
      
       <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        
        
        <div className="flex-1 p-4 lg:p-8 overflow-y-auto">
          {/* ✅ ADD HEADER HERE */}
        <Header setSidebarOpen={setSidebarOpen} />
          {/* REMOVE THE OLD HEADER - Delete these lines: */}
          {/* <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <button 
                className="lg:hidden mr-3 p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-800">Scheduled Posts</h1>
                <p className="text-sm text-gray-600">
                  Manage your content calendar • 
                  <span className="ml-2 inline-flex items-center">
                    <span className="w-3 h-3 bg-yellow-300 rounded-full mr-1"></span>
                    Scheduled
                    <span className="w-3 h-3 bg-green-300 rounded-full ml-3 mr-1"></span>
                    Posted
                  </span>
                </p>
              </div>
            </div>
            <button className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
              <User className="w-4 h-4" />
            </button>
          </div> */}

          {/* ✅ ADD NEW PAGE TITLE */}
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-800">Scheduled Posts</h1>
            <p className="text-sm text-gray-600">
              Manage your content calendar • 
              <span className="ml-2 inline-flex items-center">
                <span className="w-3 h-3 bg-yellow-300 rounded-full mr-1"></span>
                Scheduled
                <span className="w-3 h-3 bg-green-300 rounded-full ml-3 mr-1"></span>
                Posted
              </span>
            </p>
          </div>

          {/* Calendar and Filters Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Calendar Section */}
            <div className="lg:col-span-3 bg-white rounded-lg shadow-sm p-6">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-800">
                  {months[currentDate.getMonth()]} {currentDate.getFullYear()}
                </h2>
                <div className="flex items-center space-x-2">
                  {loading && (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                  )}
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setCurrentDate(new Date())}
                    className="px-4 py-2 text-sm bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    Today
                  </button>
                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-2">
                {/* Day Headers */}
                {['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'].map(day => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                    {day}
                  </div>
                ))}

                {/* Empty cells */}
                {blanks.map((_, index) => (
                  <div key={`blank-${index}`} className="aspect-square" />
                ))}

                {/* Calendar Days */}
                {days.map(day => {
                  const posts = getPostsForDate(day);
                  const isToday = 
                    day === new Date().getDate() &&
                    currentDate.getMonth() === new Date().getMonth() &&
                    currentDate.getFullYear() === new Date().getFullYear();

                  return (
                    <div
                      key={day}
                      onDoubleClick={() => handleDateDoubleClick(day)}
                      className={`aspect-square border rounded-lg p-2 cursor-pointer ${
                        isToday ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      } hover:border-gray-300 transition-colors overflow-hidden`}
                    >
                      <div className="text-sm font-medium text-gray-700 mb-1">{day}</div>
                      <div className="space-y-1 overflow-y-auto max-h-24">
                        {posts.map(post => (
                          <div
                            key={post.id}
                            className={`${post.color} rounded px-2 py-1 text-xs cursor-pointer transition-all border ${
                              selectedPost?.id === post.id ? 'ring-2 ring-blue-500' : ''
                            }`}
                            onClick={(e) => handlePostClick(post, e)}
                            title={post.title}
                          >
                            <div className="font-medium truncate">{post.title}</div>
                            <div className="text-gray-600 flex items-center text-xs">
                              <Clock className="w-3 h-3 mr-1" />
                              {post.time}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Filters and Actions Sidebar */}
            <div className="lg:col-span-1 space-y-4">
              {/* Filters */}
              {/* Filters */}
<div className="bg-white rounded-lg shadow-sm p-4">
  <div className="flex items-center justify-between mb-4">
    <h3 className="font-semibold text-gray-800">Filters</h3>
    <Filter className="w-4 h-4 text-gray-500" />
  </div>
  
  <div className="space-y-3">
    <label className="flex items-center space-x-2 cursor-pointer">
      <input
        type="checkbox"
        checked={selectedFilters.scheduled}
        onChange={() => toggleFilter('scheduled')}
        className="w-4 h-4 text-blue-600 rounded"
      />
      <span className="text-sm text-gray-700">Scheduled Only</span>
    </label>
    
    <label className="flex items-center space-x-2 cursor-pointer">
      <input
        type="checkbox"
        checked={selectedFilters.posted}
        onChange={() => toggleFilter('posted')}
        className="w-4 h-4 text-blue-600 rounded"
      />
      <span className="text-sm text-gray-700">Posted Only</span>
    </label>
    
    {/* <label className="flex items-center space-x-2 cursor-pointer">
      <input
        type="checkbox"
        checked={selectedFilters.upcoming}
        onChange={() => toggleFilter('upcoming')}
        className="w-4 h-4 text-blue-600 rounded"
      />
      <span className="text-sm text-gray-700">Upcoming Only</span>
    </label> */}
  </div>

  <div className="flex space-x-2 mt-4">
    <button 
      onClick={applyFilters}
      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
    >
      Apply
    </button>
    <button 
      onClick={() => {
        setSelectedFilters({ scheduled: false, posted: false, upcoming: false });
      }}
      className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm"
    >
      Clear
    </button>
  </div>
</div>

              {/* Group Posts */}
<div className="bg-white rounded-lg shadow-sm p-4">
  <div className="flex items-center justify-between mb-3">
    <h3 className="font-semibold text-gray-800">Group Posts</h3>
    <Users className="w-4 h-4 text-gray-500" />
  </div>

  {expandedBatchId ? (
    <div>
      <button
        onClick={closeBatchDetail}
        className="text-xs text-blue-600 hover:underline mb-3"
      >
        ← Back to all group posts
      </button>

      {isLoadingBatchDetail && !expandedBatchDetail ? (
        <p className="text-xs text-gray-400">Loading...</p>
      ) : !expandedBatchDetail ? (
        <p className="text-xs text-gray-400">Could not load this batch's details.</p>
      ) : (
        <div>
          <p className="text-xs text-gray-700 mb-2">{expandedBatchDetail.batch.content}</p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {expandedBatchDetail.jobs.map((job) => {
              const jobBadgeClass =
                job.status === 'posted' ? 'bg-green-100 text-green-700' :
                job.status === 'failed' ? 'bg-red-100 text-red-700' :
                'bg-yellow-100 text-yellow-700';
              const jobBadgeLabel =
                job.status === 'posted' ? 'Posted' :
                job.status === 'failed' ? 'Failed' :
                'Pending';
              return (
                <div key={job.id} className="border border-gray-100 rounded-lg p-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-800 truncate">{job.group_name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ml-2 ${jobBadgeClass}`}>
                      {jobBadgeLabel}
                    </span>
                  </div>
                  {job.status === 'failed' && job.error_message && (
                    <p className="text-[11px] text-red-600 mt-1">{job.error_message}</p>
                  )}
                  {job.status === 'failed' && (
                    <button
                      onClick={() => retryGroupJob(job.id)}
                      disabled={retryingJobId === job.id}
                      className="mt-1.5 text-[11px] text-blue-600 hover:underline disabled:opacity-50 disabled:no-underline"
                    >
                      {retryingJobId === job.id ? 'Retrying...' : 'Retry'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  ) : isLoadingBatches && groupBatches.length === 0 ? (
    <p className="text-xs text-gray-400">Loading...</p>
  ) : groupBatches.length === 0 ? (
    <p className="text-xs text-gray-400">No group posts yet.</p>
  ) : (
    <div className="space-y-3 max-h-72 overflow-y-auto">
      {groupBatches.map((batch) => {
        const pct = batch.total_groups > 0
          ? Math.round(((batch.completed_count + batch.failed_count) / batch.total_groups) * 100)
          : 0;
        const badgeClass =
          batch.status === 'completed' ? 'bg-green-100 text-green-700' :
          batch.status === 'completed_with_errors' ? 'bg-orange-100 text-orange-700' :
          'bg-yellow-100 text-yellow-700';
        const badgeLabel =
          batch.status === 'completed' ? 'Done' :
          batch.status === 'completed_with_errors' ? 'Done w/ errors' :
          'In progress';
        return (
          <div
            key={batch.id}
            onClick={() => openBatchDetail(batch.id)}
            className="border border-gray-100 rounded-lg p-2 cursor-pointer hover:border-blue-300 hover:bg-blue-50/50 transition-colors"
          >
            <p className="text-xs text-gray-700 truncate mb-1">{batch.content}</p>
            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1">
              <div className="bg-blue-600 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-gray-500">
                {batch.completed_count}/{batch.total_groups} posted
                {batch.failed_count > 0 && `, ${batch.failed_count} failed`}
              </span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${badgeClass}`}>
                {badgeLabel}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  )}
</div>

              {/* Action Buttons */}
<div className="bg-white rounded-lg shadow-sm p-4 space-y-3">
  <button
    onClick={() => {
      setPreviewPost(selectedPost);
      setShowPostPreview(true);
    }}
    disabled={!selectedPost}
    className={`w-full flex items-center justify-center px-4 py-2 border rounded-lg transition-colors text-sm ${
      !selectedPost
        ? 'border-gray-300 text-gray-400 cursor-not-allowed' 
        : 'border-blue-600 text-blue-600 hover:bg-blue-50'
    }`}
  >
    <Eye className="w-4 h-4 mr-2" />
    View Post
  </button>
  
  <button
    onClick={handleEditPost}
    disabled={!selectedPost || selectedPost.status === 'posted'}
    className={`w-full flex items-center justify-center px-4 py-2 border rounded-lg transition-colors text-sm ${
      !selectedPost || selectedPost.status === 'posted'
        ? 'border-gray-300 text-gray-400 cursor-not-allowed' 
        : 'border-blue-600 text-blue-600 hover:bg-blue-50'
    }`}
  >
    <Edit className="w-4 h-4 mr-2" />
    Edit Post
  </button>
  
  <button
    onClick={handleReschedule}
    disabled={!selectedPost || selectedPost.status === 'posted'}
    className={`w-full flex items-center justify-center px-4 py-2 border rounded-lg transition-colors text-sm ${
      !selectedPost || selectedPost.status === 'posted'
        ? 'border-gray-300 text-gray-400 cursor-not-allowed' 
        : 'border-blue-600 text-blue-600 hover:bg-blue-50'
    }`}
  >
    <Clock className="w-4 h-4 mr-2" />
    Reschedule
  </button>
</div>

              {/* Add New Post Button */}
              <button
                onClick={handleAddNewPost}
                className="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add New Post
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Post Preview Modal */}
      <PostPreviewModal />
    </div>
  );
};

export default SchedulePostsPage;